// src/components/bar/ProductList.js
import React, { useMemo, useEffect, useState } from 'react';
import { ref, onValue } from 'firebase/database';
import { contentUsersDatabase } from '../../firebase';
import styles from './ProductList.module.css';

// Firebase Storageからの画像URLを構築する関数
const getImageUrl = (imagePath) => {
  // 既にフルURLの場合はそのまま返す
  if (imagePath && (imagePath.startsWith('http://') || imagePath.startsWith('https://'))) {
    return imagePath;
  }

  // 画像パスがない場合はプレースホルダーを返す
  if (!imagePath) {
    return "/api/placeholder/400/300";
  }

  // ファイル名のみの場合はFirebase StorageのURLを構築
  const baseUrl = "https://firebasestorage.googleapis.com/v0/b/auth-app-4dd90.firebasestorage.app/o/";
  const encodedPath = encodeURIComponent(imagePath);
  const tokenParam = "alt=media";

  const fullUrl = `${baseUrl}${encodedPath}?${tokenParam}`;

  return fullUrl;
};

const ProductList = ({ products: productsFromProps, productsFromDatabase = true, onProductClick, orderingProduct = null }) => {
  const [advertisements, setAdvertisements] = useState([]);
  const [products, setProducts] = useState([]);

  // 広告データを新しいDBから取得
  useEffect(() => {

    // 新しいDBの構造に合わせて広告データを取得
    const adsRef = ref(contentUsersDatabase, 'advertisements/items');

    const unsubscribe = onValue(adsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        // オブジェクトを配列に変換
        const adsArray = Object.keys(data).map(key => ({
          id: key,
          ...data[key],
          isAd: true // 広告フラグを追加
        }));

        // アクティブな広告のみフィルタリング
        const now = new Date();
        const activeAds = adsArray.filter(ad => {
          // 非表示の広告は除外 (statusがhiddenか、isHiddenがtrueの場合)
          if (ad.status === 'hidden' || ad.isHidden) return false;

          // 開始日が設定されていて、まだ開始前の場合は除外
          if (ad.startDate && new Date(ad.startDate) > now) return false;

          // 終了日が設定されていて、既に終了している場合は除外
          if (ad.endDate && new Date(ad.endDate) < now) return false;

          return true;
        });

        // 優先度順にソート
        const sortedAds = activeAds.sort((a, b) => {
          return (a.priority || 1) - (b.priority || 1);
        });

        setAdvertisements(sortedAds);
      } else {
        setAdvertisements([]);
      }
    });

    return () => unsubscribe();
  }, []);

  // 製品データを新しいDBから取得
  useEffect(() => {
    // 新しいDBの構造に合わせて製品データを取得
    const productsRef = ref(contentUsersDatabase, 'products/items');

    onValue(productsRef, (snapshot) => {
      try {
        const data = snapshot.val();
        if (data) {
          // オブジェクトを配列に変換
          const productsArray = Object.keys(data).map(key => ({
            id: key,
            ...data[key],
            // 画像URLを確認
            image: data[key].imageUrl || data[key].image, // 互換性のために両方をチェック
            // isHiddenかstatusを見て表示/非表示を決定
            isHidden: data[key].status === 'hidden' || data[key].isHidden === true,
            // sortOrderを追加（未設定の場合は999を設定）
            sortOrder: data[key].sortOrder !== undefined ? data[key].sortOrder : 999
          }));

          setProducts(productsArray);
        } else {
          setProducts([]);
        }
      } catch (error) {
        console.error('商品データの取得エラー:', error);
      }
    });
  }, []);

  // 実際に表示する製品のデータソースを選択（propsから渡されたものか、DBから取得したもの）
  const displayProducts = productsFromProps && productsFromProps.length > 0 ? productsFromProps : products;

  // 広告商品をクリックしたときの処理
  const handleAdClick = (adUrl) => {
    if (adUrl) {
      // 新しいタブでリンクを開く
      window.open(adUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // 商品カードのクラス名を決定
  const getProductCardClass = (product) => {
    const isOrdering = orderingProduct && orderingProduct.id === product.id;
    const isAd = product.isAd === true;

    let cardClass = styles.productCard;

    if (isOrdering) {
      cardClass += ` ${styles.productCardOrdering}`;
    }

    if (isAd) {
      cardClass += ` ${styles.productCardAd}`;
    }

    return cardClass;
  };

  // 商品と広告を組み合わせてタイプ別にグループ化（並び替え順序を考慮）
  const organizedProducts = useMemo(() => {
    // console.log('ProductList: displayProducts:', displayProducts); // デバッグログ

    // 非表示でない商品のみをフィルタリング
    const visibleProducts = displayProducts.filter(p => !p.isHidden);
    // console.log('ProductList: visibleProducts:', visibleProducts); // デバッグログ

    // タイプ別にグループ化
    const groupedByType = {};

    // まず通常商品をタイプ別にグループ化
    visibleProducts.forEach(product => {
      const type = product.type || product.category || '未分類';
      if (!groupedByType[type]) {
        groupedByType[type] = [];
      }
      groupedByType[type].push(product);
    });

    // console.log('ProductList: groupedByType before sorting:', groupedByType); // デバッグログ

    // 各タイプ内で商品をsortOrderでソート
    Object.keys(groupedByType).forEach(type => {
      groupedByType[type].sort((a, b) => {
        // sortOrderの値を確認（デバッグ）
        const sortOrderA = a.sortOrder !== undefined ? a.sortOrder : 999;
        const sortOrderB = b.sortOrder !== undefined ? b.sortOrder : 999;

        // console.log(`ProductList: Sorting ${a.name}(${sortOrderA}) vs ${b.name}(${sortOrderB})`); // デバッグログ

        if (sortOrderA !== sortOrderB) {
          return sortOrderA - sortOrderB;
        }

        // sortOrderが同じ場合はIDでソート
        return (a.id || '').localeCompare(b.id || '');
      });

      // console.log(`ProductList: Sorted products in ${type}:`, groupedByType[type].map(p => `${p.name}(${p.sortOrder})`)); // デバッグログ
    });

    // 広告を各タイプに振り分け
    advertisements.forEach(ad => {
      const position = ad.position || 'all';

      if (position === 'all') {
        // すべてのタイプに追加
        Object.keys(groupedByType).forEach(type => {
          groupedByType[type].push({ ...ad, type });
        });
      } else if (position === 'alcohol' || position === 'nonalcohol' || position === 'food') {
        // 特定のタイプにのみ追加
        // タイプを文字列から対応するカテゴリ文字列に変換
        const typeMapping = {
          'alcohol': 'アルコール',
          'nonalcohol': 'ノンアルコール',
          'food': 'フード'
        };

        const mappedType = typeMapping[position];

        if (mappedType && groupedByType[mappedType]) {
          groupedByType[mappedType].push({ ...ad, type: mappedType });
        } else if (!groupedByType[mappedType]) {
          // 対応するタイプが存在しない場合は作成
          groupedByType[mappedType] = [{ ...ad, type: mappedType }];
        }
      }
    });

    // 結果を格納する配列（タイプごとにセクション分け）
    const result = [];

    // 各タイプのセクションを作成
    const sectionKeys = Object.keys(groupedByType).sort();

    sectionKeys.forEach(type => {
      // 各カテゴリの商品を結果に追加
      result.push({
        type: type,
        products: groupedByType[type]
      });
    });

    return result;
  }, [displayProducts, advertisements]);

  return (
    <div className={styles.productContainer}>
      {organizedProducts.map((section, sectionIndex) => (
        <div key={`section-${sectionIndex}`} className={styles.productSection}>
          <h2 className={styles.sectionTitle}>{section.type}</h2>
          <div className={styles.productGrid}>
            {section.products.map((product, productIndex) => {
              const isOrdering = orderingProduct && orderingProduct.id === product.id;
              const isAd = product.isAd === true;

              return (
                <div
                  key={`${sectionIndex}-${productIndex}-${product.id}`}
                  className={getProductCardClass(product)}
                  onClick={() => {
                    if (isAd && product.adUrl) {
                      handleAdClick(product.adUrl);
                    } else {
                      onProductClick(product);
                    }
                  }}
                >
                  <div className={styles.productCardContent}>
                    {isOrdering && (
                      <div className={styles.orderingBadge}>
                        製作中
                      </div>
                    )}
                    {isAd && (
                      <div className={styles.adBadge}>
                        広告
                      </div>
                    )}
                    <div className={styles.productImageContainer}>
                      <img
                        src={getImageUrl(product.image || product.imageUrl)}
                        alt={product.name}
                        className={styles.productImage}
                      />
                    </div>
                    <div className={styles.productInfo}>
                      <h3 className={styles.productName}>{product.name}</h3>
                      <p className={styles.productPrice}>
                        {isAd ? (product.adLabel || 'スポンサー商品') : (
                          <>
                            ¥{product.price}
                            {!productsFromDatabase && <span className={styles.localInfoBadge}> ローカル情報表示</span>}
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ProductList;